import React, { useEffect, useRef } from "react";

import Status from "@/components/status";
import { PreviewProvider } from "@/contexts/preview-context";

import type { Status as StatusEntity } from "@/schemas";

interface PreviewStatusProps {
  status: StatusEntity;
}

/**
 * Non-interactive status for decorative surfaces (e.g. the landing page).
 * Renders the real <Status> in preview mode (no action bar, no translation,
 * no navigation) and hard-disables interaction for the whole subtree via
 * `inert` + `pointer-events-none`, so nothing inside is clickable or focusable.
 */
const PreviewStatus: React.FC<PreviewStatusProps> = ({ status }) => {
  const ref = useRef<HTMLDivElement>(null);

  // `inert` isn't a typed React 18 prop, so set it imperatively. It blocks
  // focus + AT for every descendant (links, buttons, embeds).
  useEffect(() => {
    if (ref.current) ref.current.inert = true;
  }, []);

  return (
    <div
      ref={ref}
      aria-hidden
      className="pointer-events-none rounded-2xl shadow-[0_0_8px_4px_rgba(84,72,238,0.08)] select-none **:data-markup:line-clamp-6 dark:border dark:border-gray-800 dark:shadow-none"
    >
      <PreviewProvider>
        <Status status={status} preview focusable={false} hoverable={false} />
      </PreviewProvider>
    </div>
  );
};

export default PreviewStatus;
